import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCompleteProgramMutation } from "@/graphql";
import { MainProgram, MiniProgram } from "@/types/programs";

interface EndProgramDialogProps {
  open: boolean;
  afterEndProgram: () => void;
  onClose: () => void;
  program: MainProgram | MiniProgram;
}

export function EndProgramDialog({
  open,
  afterEndProgram,
  onClose,
  program,
}: EndProgramDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const { id: programId, name } = program || {};

  const [completeProgram, { loading }] = useCompleteProgramMutation({
    onCompleted: (data) => {
      const { ok, msg } = data?.completeProgram || {};
      if (ok) {
        afterEndProgram();
        onClose();
      } else {
        setErrorMessage(msg || "Something went wrong. Please try again.");
      }
    },
    onError: (err) => {
      setErrorMessage(err?.message);
    },
  });

  function handleClose() {
    setErrorMessage(undefined);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>End Program Confirmation</DialogTitle>
        </DialogHeader>
        <DialogInner className="leading-6">
          {errorMessage && (
            <div className="mb-2">
              <Callout variant="error" icon={null}>
                {errorMessage}
              </Callout>
            </div>
          )}

          <div className="mb-2">
            Are you sure you want to end <strong>"{name}"</strong>?
          </div>
          <div className="mb-2">This will:</div>
          <ul className="list-disc pl-6">
            <li>Mark the program as Complete</li>
            <li>
              End all ongoing components (no additional notification emails will
              be sent)
            </li>
            <li>Remove any upcoming or in-design components</li>
          </ul>
        </DialogInner>
        <DialogFooter>
          <Button
            intent="primary"
            disabled={loading}
            onClick={() => completeProgram({ variables: { programId } })}
          >
            Yes, End Program
          </Button>
          <Button intent="secondary" onClick={handleClose} disabled={loading}>
            Nevermind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
